import { CATEGORY_LABELS } from "./types";
import { FolderOpen, Tag, Hash } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface MediaSidebarProps {
  categoryCounts: Record<string, number>;
  activeCategory: string | null;
  onCategorySelect: (cat: string | null) => void;
  allTags: { tag: string; count: number }[];
  activeTags: string[];
  onTagToggle: (tag: string) => void;
}

// Group categories for the accordion
const CATEGORY_GROUPS: { label: string; keys: string[] }[] = [
  { label: "News & World", keys: ["politics-world", "economy-trade", "sports", "lifestyle-society"] },
  { label: "Tech & Innovation", keys: ["tech-ai", "cyber-security", "programming", "cyber-physical", "space-aero"] },
  { label: "Business", keys: ["corporate-brands", "entrepreneurship"] },
  { label: "Science & Health", keys: ["science"] },
  { label: "Branding", keys: ["brand-logos", "logos", "icons"] },
  { label: "Media", keys: ["backgrounds", "product-photos", "illustrations", "screenshots", "banners"] },
];

export default function MediaSidebar({
  categoryCounts,
  activeCategory,
  onCategorySelect,
  allTags,
  activeTags,
  onTagToggle,
}: MediaSidebarProps) {
  const totalCount = Object.values(categoryCounts).reduce((a, b) => a + b, 0);

  // Determine which groups have items
  const activeGroups = CATEGORY_GROUPS
    .map((g) => ({ ...g, total: g.keys.reduce((s, k) => s + (categoryCounts[k] || 0), 0) }))
    .filter((g) => g.total > 0);

  // Categories not in any group
  const groupedKeys = new Set(CATEGORY_GROUPS.flatMap((g) => g.keys));
  const ungrouped = Object.keys(categoryCounts).filter(
    (k) => !groupedKeys.has(k) && k !== "uncategorized" && (categoryCounts[k] || 0) > 0
  );

  return (
    <div className="w-60 flex-shrink-0 border-r border-border flex flex-col h-full">
      <ScrollArea className="flex-1">
        {/* All files */}
        <div className="p-3 border-b border-border">
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-2">
            <FolderOpen className="h-3 w-3" /> Categories
          </h3>
          <button
            onClick={() => onCategorySelect(null)}
            className={`w-full flex items-center justify-between rounded-md px-2 py-1.5 text-xs transition-colors ${
              activeCategory === null
                ? "bg-primary/10 text-primary font-medium"
                : "text-foreground hover:bg-accent"
            }`}
          >
            <span>All Files</span>
            <span className="text-[10px] text-muted-foreground">{totalCount}</span>
          </button>

          {/* Uncategorized */}
          {(categoryCounts["uncategorized"] || 0) > 0 && (
            <button
              onClick={() => onCategorySelect("uncategorized")}
              className={`w-full flex items-center justify-between rounded-md px-2 py-1.5 text-xs transition-colors ${
                activeCategory === "uncategorized"
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-foreground hover:bg-accent"
              }`}
            >
              <span>{CATEGORY_LABELS["uncategorized"] || "Uncategorized"}</span>
              <span className="text-[10px] text-muted-foreground">{categoryCounts["uncategorized"]}</span>
            </button>
          )}
        </div>

        {/* Grouped categories */}
        <div className="px-2 py-1">
          <Accordion type="multiple" defaultValue={activeGroups.map((_, i) => `group-${i}`)} className="space-y-0">
            {activeGroups.map((group, gi) => (
              <AccordionItem key={gi} value={`group-${gi}`} className="border-b-0">
                <AccordionTrigger className="py-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:no-underline">
                  <span className="flex items-center gap-1.5">
                    {group.label}
                    <span className="text-[9px] font-normal opacity-60">({group.total})</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-1 pt-0">
                  <div className="space-y-0.5">
                    {group.keys.map((cat) => {
                      const count = categoryCounts[cat] || 0;
                      if (count === 0) return null;
                      return (
                        <button
                          key={cat}
                          onClick={() => onCategorySelect(cat)}
                          className={`w-full flex items-center justify-between rounded-md px-2 py-1.5 text-xs transition-colors ${
                            activeCategory === cat
                              ? "bg-primary/10 text-primary font-medium"
                              : "text-foreground hover:bg-accent"
                          }`}
                        >
                          <span>{CATEGORY_LABELS[cat] || cat}</span>
                          <span className="text-[10px] text-muted-foreground">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Ungrouped */}
          {ungrouped.length > 0 && (
            <div className="mt-1 space-y-0.5 px-1">
              {ungrouped.map((cat) => (
                <button
                  key={cat}
                  onClick={() => onCategorySelect(cat)}
                  className={`w-full flex items-center justify-between rounded-md px-2 py-1.5 text-xs transition-colors ${
                    activeCategory === cat
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-foreground hover:bg-accent"
                  }`}
                >
                  <span>{CATEGORY_LABELS[cat] || cat}</span>
                  <span className="text-[10px] text-muted-foreground">{categoryCounts[cat]}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tags */}
        <div className="p-3 border-t border-border">
          <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mb-2">
            <Tag className="h-3 w-3" /> Tags
          </h3>
          {allTags.length === 0 ? (
            <p className="text-[10px] text-muted-foreground italic">No tags</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {allTags.slice(0, 40).map(({ tag, count }) => (
                <button key={tag} onClick={() => onTagToggle(tag)}>
                  <Badge
                    variant={activeTags.includes(tag) ? "default" : "outline"}
                    className="text-[10px] cursor-pointer hover:bg-primary/20 transition-colors gap-1"
                  >
                    <Hash className="h-2.5 w-2.5" />
                    {tag}
                    <span className="opacity-60">{count}</span>
                  </Badge>
                </button>
              ))}
              {allTags.length > 40 && (
                <span className="text-[10px] text-muted-foreground italic">+{allTags.length - 40} tags</span>
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
